import type { AccountView, AuthResponse, GuildView, LeaderboardEntry, LeaderboardKind, NoticeView, PlayerPresence, SaveOp, Visibility } from '@knight/shared';

const BASE = '/api';

/** `?tab=1` keeps identity per browser tab (sessionStorage) so several accounts can be tested at once. */
const store: Storage = new URLSearchParams(location.search).has('tab') ? sessionStorage : localStorage;

export function getToken(): string | null {
  return store.getItem('knight.token');
}

export function setToken(t: string | null): void {
  if (t) store.setItem('knight.token', t);
  else store.removeItem('knight.token');
}

export function deviceId(): string {
  let d = store.getItem('knight.device');
  if (!d) {
    d = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    store.setItem('knight.device', d);
  }
  return d;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly key: string,
    message?: string,
  ) {
    super(message ?? key);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  // a hung server must not freeze the client forever: offline mode takes over after the timeout
  const res = await fetch(BASE + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal: AbortSignal.timeout(10000) });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'error', data?.message);
  return data as T;
}

export const api = {
  health: () => req<{ ok: boolean; players: number; google: boolean; origin: string }>('GET', '/health'),
  guest: (lang: string, ref?: string | null) => req<AuthResponse>('POST', '/auth/guest', { device: deviceId(), lang, ref: ref ?? undefined }),
  me: () => req<AccountView>('GET', '/me'),
  google: (idToken: string, merge: boolean) => req<AuthResponse & { conflict?: boolean; other?: { name: string; fame: number } }>('POST', '/auth/google', { idToken, merge, device: deviceId() }),
  signout: () => req<{ ok: boolean }>('POST', '/auth/signout'),
  updateMe: (patch: { name?: string; avatar?: number; lang?: string; favoriteHero?: string; visibility?: Visibility; showMode?: boolean }) => req<AccountView>('PATCH', '/me', patch),
  player: (name: string) => req<PlayerPresence>('GET', '/players/' + encodeURIComponent(name)),
  vaultStash: (slot: number, index: number) => req<AccountView>('POST', '/vault/stash', { slot, index }),
  vaultTake: (slot: number, index: number) => req<AccountView>('POST', '/vault/take', { slot, index }),
  vaultSalvage: (index: number) => req<AccountView>('POST', '/vault/salvage', { index }),
  vaultSlotBuy: () => req<AccountView>('POST', '/vault/slot'),
  notices: () => req<NoticeView[]>('GET', '/me/notices'),
  guildCreate: (name: string, tag: string) => req<GuildView>('POST', '/guilds', { name, tag }),
  guildJoin: (code: string) => req<GuildView>('POST', '/guilds/join', { code }),
  guildLeave: () => req<{ ok: boolean }>('POST', '/guilds/leave'),
  guildKick: (accountId: string) => req<GuildView>('POST', '/guilds/kick', { accountId }),
  guildMe: () => req<GuildView | null>('GET', '/guilds/me'),
  leaderboard: (kind: LeaderboardKind) => req<LeaderboardEntry[]>('GET', '/leaderboards/' + kind),
  ghost: (id: string) => req<{ id: string; name: string; heroId: string; level: number; kills: number; slain: number }>('GET', '/ghosts/' + encodeURIComponent(id)),
  petSelect: (id: string) => req<AccountView>('POST', '/pets/select', { id }),
  petUnlock: (id: string) => req<AccountView>('POST', '/pets/unlock', { id }),
  heroSlotBuy: () => req<AccountView>('POST', '/heroes/slot'),
  heroDelete: (slot: number) => req<AccountView>('POST', '/heroes/delete', { slot }),
  heroBuild: (slot: number, op: SaveOp) => req<AccountView>('POST', '/heroes/build', { slot, op }),
  event: (name: string, props?: Record<string, unknown>) => req<{ ok: boolean }>('POST', '/events', { name, props }).catch(() => ({ ok: false })),
};
